import * as Sentry from "@sentry/vue";
import type { AuthTokens } from "@/shared/auth/@types/AuthTokens";
import { clearCookie } from "@/shared/helper/clearCookie.helper";
import { getEnvValue } from "@/shared/helper/cookieAttributes.helper";
import { readCookie } from "@/shared/helper/readCookie.helper";
import { serializeCookie, setCookie } from "@/shared/helper/setCookie.helper";

const COOKIE_NAME = "authTokens";

export const setAuthTokensCookie = (tokens: AuthTokens): void => {
  const cookieData = {
    ...tokens,
    env: getEnvValue(),
  };
  const serializedCookieData = JSON.stringify(cookieData);
  const expirationMs = 7 * 24 * 60 * 60 * 1000;
  const cookieIsSet = setCookie({
    name: COOKIE_NAME,
    value: serializedCookieData,
    expirationMs,
  });

  if (!cookieIsSet) {
    Sentry.captureMessage("Auth tokens cookie could not be set", {
      level: "error",
      extra: {
        cookieName: COOKIE_NAME,
        ...getCookieDiagnostics(serializedCookieData, expirationMs),
      },
    });
  }
};

export const getAuthTokensFromCookie = (): AuthTokens | undefined => {
  const currentEnv = getEnvValue();
  const cookieData = readCookie({
    name: COOKIE_NAME,
    env: currentEnv,
  });

  if (!cookieData) return undefined;

  return {
    idToken: cookieData.idToken,
    accessToken: cookieData.accessToken,
    refreshToken: cookieData.refreshToken,
  };
};

export const clearAuthTokensCookie = (): void => {
  clearCookie(COOKIE_NAME);
};

const getByteLength = (value: string): number => {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(value).length;
  }

  return value.length;
};

const getCookieDiagnostics = (cookieValue: string, expirationMs: number) => {
  if (typeof document === "undefined") {
    return {
      documentAvailable: false,
    };
  }

  const {
    cookieString: attemptedCookieString,
    encodedValue: encodedCookieValue,
    expirationDate,
    domainAttribute,
    secureAttribute,
  } = serializeCookie({
    name: COOKIE_NAME,
    value: cookieValue,
    expirationMs,
  });

  const rawDocumentCookie = document.cookie ?? "";
  const cookies = rawDocumentCookie ? rawDocumentCookie.split("; ") : [];
  const cookieSizes = cookies
    .map((cookie) => {
      const separatorIndex = cookie.indexOf("=");
      const cookieName =
        separatorIndex >= 0 ? cookie.slice(0, separatorIndex) : cookie;

      return {
        name: cookieName,
        sizeBytes: getByteLength(cookie),
      };
    })
    .sort((a, b) => b.sizeBytes - a.sizeBytes);

  const authTokenCookieMatches = cookies.filter((cookie) =>
    cookie.startsWith(`${COOKIE_NAME}=`),
  );

  const isTopLevelFrame = (() => {
    try {
      return window.top === window.self;
    } catch {
      return false;
    }
  })();

  return {
    documentAvailable: true,
    attemptedCookieSizeBytes: getByteLength(attemptedCookieString),
    attemptedCookieValueSizeBytes: getByteLength(encodedCookieValue),
    attemptedCookieAttributes: {
      domain: domainAttribute,
      path: "/",
      sameSite: "Strict",
      secure: secureAttribute.includes("Secure"),
      expires: expirationDate,
    },
    existingCookiesCount: cookies.length,
    existingCookiesTotalSizeBytes: getByteLength(rawDocumentCookie),
    existingLargestCookieSizeBytes: cookieSizes[0]?.sizeBytes,
    existingLargestCookies: cookieSizes.slice(0, 10),
    existingAuthTokenCookieCount: authTokenCookieMatches.length,
    existingAuthTokenCookieSizesBytes: authTokenCookieMatches.map((cookie) =>
      getByteLength(cookie),
    ),
    cookiesEnabledInBrowser: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    globalPrivacyControl:
      "globalPrivacyControl" in navigator
        ? navigator.globalPrivacyControl
        : undefined,
    language: navigator.language,
    languages: navigator.languages,
    platform: navigator.platform,
    userAgent: navigator.userAgent,
    isSecureContext: window.isSecureContext,
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    isTopLevelFrame,
  };
};
